import { useState, useEffect, useRef, useCallback } from 'react';
import { TokenManager } from '../utils/tokenManager';
import { toast } from 'react-toastify';

// 스마트 하이브리드 토큰 관리 훅
const useTokenManager = () => {
    const [isIdle, setIsIdle] = useState(false);
    const [lastActivity, setLastActivity] = useState(Date.now());
    const intervalRef = useRef(null);
    const idleTimeoutRef = useRef(null);

    // 설정
    const PERIODIC_CHECK_INTERVAL = 5 * 60 * 1000; // 5분마다 체크
    const IDLE_TIMEOUT = 5 * 60 * 1000; // 5분 비활성 시 idle 상태

    // 사용자 활동 감지
    const handleActivity = useCallback(() => {
        const now = Date.now();
        setLastActivity(now);

        if (isIdle) {
            setIsIdle(false);
        }

        // idle 타이머 리셋
        if (idleTimeoutRef.current) {
            clearTimeout(idleTimeoutRef.current);
        }

        idleTimeoutRef.current = setTimeout(() => {
            setIsIdle(true);
        }, IDLE_TIMEOUT);
    }, [isIdle, IDLE_TIMEOUT]);

    // 주기적 토큰 체크 함수
    const performPeriodicTokenCheck = useCallback(() => {
        if (isIdle) {
            return;
        }

        const token = sessionStorage.getItem('token');
        const siteToken = sessionStorage.getItem('siteToken');

        if (token && TokenManager.isTokenExpiringsoon('token')) {
            // 중복 갱신 방지 체크
            const now = Date.now();
            const canRefresh = !TokenManager._refreshInProgress &&
                (now - TokenManager._lastRefreshTime) > TokenManager._refreshCooldown;

            if (canRefresh) {
                TokenManager._refreshInProgress = true;
                TokenManager._lastRefreshTime = now;

                TokenManager.refreshToken(true)
                    .then(() => {
                        // 백그라운드 갱신 성공 - 조용히 처리
                    })
                    .catch(() => {
                        // 백그라운드 갱신 실패 - 조용히 처리
                    })
                    .finally(() => {
                        TokenManager._refreshInProgress = false;
                    });
            }
        }
    }, [isIdle]);

    // 활동 감지 이벤트 리스너 등록
    useEffect(() => {
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

        // 이벤트 리스너 등록
        events.forEach(event => {
            document.addEventListener(event, handleActivity, { passive: true });
        });

        // 초기 활동 설정
        handleActivity();

        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleActivity);
            });

            if (idleTimeoutRef.current) {
                clearTimeout(idleTimeoutRef.current);
            }
        };
    }, [handleActivity]);

    // 주기적 토큰 체크 인터벌 설정
    useEffect(() => {
        intervalRef.current = setInterval(performPeriodicTokenCheck, PERIODIC_CHECK_INTERVAL);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [performPeriodicTokenCheck, PERIODIC_CHECK_INTERVAL]);

    // 상태 정보 반환 (디버깅용)
    return {
        isIdle,
        lastActivity: new Date(lastActivity).toLocaleTimeString(),
        periodicCheckActive: !isIdle,
        nextCheckIn: isIdle ? '비활성 (일시정지)' : '5분마다'
    };
};

export default useTokenManager;
